import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

export const EASE = [0.16, 1, 0.3, 1];

export function MaskedLines({ lines, className = "", delay = 0.25, stagger = 0.12, accentIndexes = [], accentClass = "text-bluebrand" }) {
    return (
        <h1 className={className} data-testid="hero-headline">
            {lines.map((line, i) => (
                <span key={i} className="block overflow-hidden pb-[0.1em] -mb-[0.1em]">
                    <motion.span
                        className={`block will-change-transform ${accentIndexes.includes(i) ? accentClass : ""}`}
                        initial={{ y: "115%" }}
                        animate={{ y: "0%" }}
                        transition={{ duration: 1.05, ease: EASE, delay: delay + i * stagger }}
                    >
                        {line}
                    </motion.span>
                </span>
            ))}
        </h1>
    );
}

export function Reveal({ children, delay = 0, y = 32, className = "" }) {
    return (
        <motion.div
            className={className}
            initial={{ opacity: 0, y }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-70px" }}
            transition={{ duration: 0.9, ease: EASE, delay }}
        >
            {children}
        </motion.div>
    );
}

export function Marquee({ items, dark = false }) {
    const row = items.map((i) => i.toUpperCase()).join("   •   ") + "   •   ";
    return (
        <div
            data-testid="editorial-marquee"
            className={`overflow-hidden border-y ${dark ? "bg-ink text-bone border-boneline" : "bg-bone border-inkline"}`}
        >
            <div className="marquee-track flex w-max whitespace-nowrap py-5">
                {[0, 1].map((k) => (
                    <span key={k} aria-hidden={k === 1} className="font-display text-sm md:text-base font-bold uppercase tracking-[0.35em]">
                        {row}
                    </span>
                ))}
            </div>
        </div>
    );
}

export function SectionTag({ children }) {
    return (
        <div className="mb-8 flex items-center gap-3" data-testid="section-tag">
            <span className="inline-block h-2.5 w-2.5 bg-bluebrand" />
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-smoke">{children}</span>
        </div>
    );
}

export function CTAButton({ to, children, variant = "blue", testid, className = "" }) {
    const base =
        "group inline-flex items-center gap-3 rounded-full px-7 py-4 font-display text-sm font-bold uppercase tracking-widest transition-[background-color,color,transform] duration-300 active:scale-[0.97]";
    const styles =
        variant === "blue"
            ? "bg-bluebrand text-white hover:bg-ink"
            : variant === "ink"
              ? "bg-ink text-bone hover:bg-bluebrand"
              : variant === "volt"
                ? "bg-volt text-ink hover:bg-ink hover:text-volt"
                : "bg-transparent text-ink border border-inkline hover:border-ink hover:bg-ink hover:text-bone";
    const inner = (
        <>
            {children}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
        </>
    );
    if (to && to.startsWith("http")) {
        return (
            <a href={to} data-testid={testid} className={`${base} ${styles} ${className}`}>
                {inner}
            </a>
        );
    }
    return (
        <Link to={to} data-testid={testid} className={`${base} ${styles} ${className}`}>
            {inner}
        </Link>
    );
}

export function ParallaxImage({ src, alt, className = "", speed = 0.12 }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], [`-${speed * 100}%`, `${speed * 100}%`]);
    return (
        <div ref={ref} className={`overflow-hidden ${className}`}>
            <motion.img
                style={{ y }}
                src={src}
                alt={alt}
                loading="lazy"
                className="h-[125%] w-full scale-110 object-cover"
            />
        </div>
    );
}
